import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import i18n from '@/libs/i18n'
import { noteModuleRefusal } from '@/libs/license'
import router from '@/router'
import { PrintError } from '@/libs/toast'
import { useAppStore } from '@/stores/app'

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: '/api/pms/v1',

        /**
         * NO CLIENT TIMEOUT, AND THAT IS DELIBERATE.
         *
         * `createFetch` takes one and this does not pass it, so nothing here abandons a
         * call the server is still answering. The service decides how long a request may
         * take, and a browser that gave up first would throw away an answer that was
         * about to arrive. On this portal it matters most for a stamp: a press abandoned
         * client-side would be retried by the queue, and the retry is only harmless
         * because it carries the same `operation_id` — but the screen would meanwhile
         * have told somebody their arrival failed when it had not.
         */
        options: {
            /**
             * KEEP THE BODY OF A FAILED CALL.
             *
             * VueUse throws it away by default, and this portal reads it everywhere:
             * every screen calls `parseError(data.value, fallback)`, which without this
             * sees null on every refusal, matches no code, and prints its generic
             * fallback. A stamp refused because the day cannot take it (40) and one
             * refused because another press is mid-write (62) would both arrive as
             * „dejanja ni bilo mogoče izvesti“ — and the second of those two means
             * „press it again“, which is the one instruction that must reach a person.
             *
             * The refusals still ARE errors: `error` is set and every `statusCode` check
             * in the portal is untouched — they all read the status first and the body
             * second. This only stops the reason from being discarded on the way.
             */
            updateDataOnError: true,

            beforeFetch({ options }) {
                options.headers = new Headers(options.headers)

                const token = appStore.getJWT()

                if (token) options.headers.set('Authorization', `Bearer ${token}`)

                return { options }
            },
            async onFetchError(ctx) {
                // The hotel has not bought the module. Noticed here, in the one place
                // every call passes through, rather than in each screen: whichever call
                // happens to be first is refused the same way, and the portal has to
                // stop rather than draw an empty week.
                //
                // On this portal it is the whole application and not one tab — every
                // screen here belongs to `staff` — so what it produces is the locked
                // notice over everything (App.vue), which is the honest picture.
                //
                // Before the 401 branch and separate from it: this is a 403, nobody is
                // signed out for it, and there is nothing to sign in to.
                noteModuleRefusal(ctx.response?.status, ctx.data)

                // A refused sign-in is not an expired session. The login screen says
                // „napačno uporabniško ime ali geslo“ itself, and adding „seja je
                // potekla“ beside it tells somebody who mistyped a password that
                // something else went wrong too.
                if (ctx.response?.status === 401 && appStore.isLoggedIn()) {
                    // Say why. On a telephone an expired session looks like the clock
                    // refusing the press, and the person presses the button again
                    // instead of signing in.
                    PrintError(i18n.global.t('errors.session_expired'))

                    appStore.setLogout()
                    await router.push({ name: 'login' })
                }

                return ctx
            },
        },
    })

    return { fetch }
})
